const primitiveColors = {
  blue: {
    50: "#ECF6FF",
    100: "#DBEEFF",
    200: "#A0CBFD",
    300: "#75ABFB",
    400: "#4A83F9",
    500: "#1E5AF6",
    600: "#1A48D0",
    700: "#2238A8",
    800: "#213288",
    900: "#19286D",
    1000: "#131E4E"
  },
  yellow: {
    50: "#FFFBE9",
    100: "#FFF7C9",
    200: "#FFF3A8",
    300: "#FFED76",
    400: "#FEE643",
    500: "#FFD12E",
    600: "#F49B26",
    700: "#E8641F",
    800: "#DD2E17",
    900: "#B72110",
    1000: "#91140A"
  },
  gray: {
    50: "#F3FAFF",
    100: "#EAF2FD",
    200: "#D5DFEE",
    300: "#BECBDF",
    400: "#A4B3CD",
    500: "#8C9CB6",
    600: "#72839E",
    700: "#5E6C84",
    800: "#465167",
    900: "#2E3646",
    1000: "#181B25"
  },
  green: {
    50: "#E4FFEE",
    100: "#C4FDD8",
    200: "#93F9BF",
    300: "#67F5AD",
    400: "#34EC99",
    500: "#0DDE8D",
    600: "#00CB88",
    700: "#00B282",
    800: "#009274",
    900: "#006E5E",
    1000: "#004741"
  },
  black: {
    0: "#181B2500",
    4: "#181B250A",
    6: "#181B250F",
    8: "#181B2514",
    12: "#181B251F",
    16: "#181B2529",
    24: "#181B253D",
    32: "#181B2552",
    40: "#181B2566",
    48: "#181B257A",
    56: "#181B258F",
    64: "#181B25A3",
    80: "#181B25CC",
    100: "#181B25"
  },
  white: {
    0: "#FFFFFF00",
    4: "#FFFFFF0A",
    6: "#FFFFFF0F",
    8: "#FFFFFF14",
    12: "#FFFFFF1F",
    16: "#FFFFFF29",
    24: "#FFFFFF3D",
    32: "#FFFFFF52",
    40: "#FFFFFF66",
    48: "#FFFFFF7A",
    56: "#FFFFFF8F",
    64: "#FFFFFFA3",
    80: "#FFFFFFCC",
    100: "#FFFFFF"
  },
  red: {
    50: "#FFE7EC",
    100: "#FFC4D5",
    200: "#FFB2C3",
    300: "#FF8D9E",
    400: "#F86077",
    500: "#E02954",
    600: "#B21F42",
    700: "#701027",
    800: "#5D0E1D",
    900: "#380909",
    1000: "#250303"
  },
  tango: {
    50: "#FFE8E0",
    100: "#FFC8B6",
    200: "#FFA98C",
    300: "#FF8961",
    400: "#FF6937",
    500: "#F74B1C",
    600: "#C03B17",
    700: "#882C13",
    800: "#511C0E",
    900: "#3E160A",
    1000: "#2B1006"
  },
  violet: {
    50: "#F7F0FF",
    100: "#ECDCFF",
    200: "#D1ACFF",
    300: "#B67CFF",
    400: "#9A4BFF",
    500: "#7F1BFF",
    600: "#6A16D6",
    700: "#5511AC",
    800: "#400D83",
    900: "#2B0859",
    1000: "#160330"
  },
  inchworm: {
    50: "#E8F6DA",
    100: "#CDECAF",
    200: "#B3E283",
    300: "#8CD345",
    400: "#6ABB3A",
    500: "#47A22F",
    600: "#258A24",
    700: "#027119",
    800: "#036513",
    900: "#044E08",
    1000: "#053D00"
  },
  aqua: {
    50: "#EEFEFD",
    100: "#D0F7F7",
    200: "#9BECED",
    300: "#66E0E3",
    400: "#59CED6",
    500: "#57B5CC",
    600: "#49A0B4",
    700: "#3C8A9D",
    800: "#2E7585",
    900: "#215F6E",
    1000: "#134A56"
  }
} as const;

export const colors = {
  bg: {
    base: {
      default: primitiveColors.white[100],
      primary: primitiveColors.blue[500],
      secondary: primitiveColors.blue[700],
      tertiary: primitiveColors.blue[900],
      onbrand: primitiveColors.white[24],
      disabled: primitiveColors.gray[50],
      ondisable: primitiveColors.gray[400]
    },
    brand: {
      primaryBold: primitiveColors.blue[500],
      secondaryBold: primitiveColors.blue[400],
      primarySoft: primitiveColors.blue[100],
      secondarySoft: primitiveColors.blue[50]
    },
    neutral: {
      primaryBold: primitiveColors.gray[800],
      secondaryBold: primitiveColors.gray[700],
      tertiary: primitiveColors.gray[300],
      primarySoft: primitiveColors.gray[100],
      secondarySoft: primitiveColors.gray[50]
    },
    information: {
      primaryBold: primitiveColors.blue[500],
      secondaryBold: primitiveColors.blue[400],
      primarySoft: primitiveColors.blue[100],
      secondarySoft: primitiveColors.blue[50]
    },
    warning: {
      primaryBold: primitiveColors.yellow[600],
      secondaryBold: primitiveColors.yellow[500],
      primarySoft: primitiveColors.yellow[100],
      secondarySoft: primitiveColors.yellow[50]
    },
    success: {
      primaryBold: primitiveColors.green[800],
      secondaryBold: primitiveColors.green[700],
      primarySoft: primitiveColors.green[100],
      secondarySoft: primitiveColors.green[50]
    },
    error: {
      primaryBold: primitiveColors.red[500],
      secondaryBold: primitiveColors.red[400],
      primarySoft: primitiveColors.red[100],
      secondarySoft: primitiveColors.red[50]
    },
    invert: {
      primaryBold: primitiveColors.white[100],
      secondaryBold: primitiveColors.white[80],
      primarySoft: primitiveColors.white[32],
      secondarySoft: primitiveColors.white[24]
    }
  },
  text: {
    base: {
      primary: primitiveColors.black[100],
      secondary: primitiveColors.gray[600],
      disabled: primitiveColors.gray[400],
      link: primitiveColors.blue[500],
      invert: primitiveColors.white[100]
    },
    state: {
      brandDefault: primitiveColors.blue[500],
      brandHover: primitiveColors.blue[400],
      brandPress: primitiveColors.blue[400],
      destructiveDefault: primitiveColors.red[500],
      destructiveHover: primitiveColors.red[400],
      destructivePress: primitiveColors.red[400],
      positiveDefault: primitiveColors.green[800],
      positiveHover: primitiveColors.green[700],
      positivePress: primitiveColors.green[700],
      invertDefault: primitiveColors.white[100],
      invertHover: primitiveColors.white[64],
      invertPress: primitiveColors.white[64]
    },
    semantic: {
      brand: primitiveColors.blue[500],
      neutral: primitiveColors.gray[800],
      information: primitiveColors.blue[500],
      warning: primitiveColors.yellow[600],
      success: primitiveColors.green[800],
      error: primitiveColors.red[500]
    }
  },
  border: {
    state: {
      default: primitiveColors.gray[200],
      hover: primitiveColors.blue[300],
      active: primitiveColors.blue[300],
      focus: primitiveColors.blue[500],
      disable: primitiveColors.gray[400],
      invert: primitiveColors.white[32]
    },
    semantic: {
      brand: primitiveColors.blue[500],
      neutral: primitiveColors.gray[800],
      information: primitiveColors.blue[500],
      warning: primitiveColors.yellow[600],
      success: primitiveColors.green[800],
      error: primitiveColors.red[500]
    }
  },
  icon: {
    base: {
      primary: primitiveColors.black[100],
      secondary: primitiveColors.gray[600],
      disabled: primitiveColors.gray[400],
      link: primitiveColors.blue[500],
      invert: primitiveColors.white[100]
    },
    state: {
      brandDefault: primitiveColors.blue[500],
      brandHover: primitiveColors.blue[400],
      brandPress: primitiveColors.blue[400],
      destructiveDefault: primitiveColors.red[500],
      destructiveHover: primitiveColors.red[400],
      destructivePress: primitiveColors.red[400],
      positiveDefault: primitiveColors.green[800],
      positiveHover: primitiveColors.green[700],
      positivePress: primitiveColors.green[700],
      invertDefault: primitiveColors.white[100],
      invertHover: primitiveColors.white[64],
      invertPress: primitiveColors.white[64]
    },
    semantic: {
      brand: primitiveColors.blue[500],
      neutral: primitiveColors.gray[800],
      information: primitiveColors.blue[500],
      warning: primitiveColors.yellow[600],
      success: primitiveColors.green[800],
      error: primitiveColors.red[500]
    }
  },
  effect: {
    shadow: primitiveColors.black[8],
    overlay: primitiveColors.black[40]
  },
  branding: {
    primary: primitiveColors.blue[500],
    secondary: primitiveColors.yellow[400],
    tango: primitiveColors.tango[500],
    magenta: primitiveColors.red[500],
    teal: primitiveColors.green[500],
    violet: primitiveColors.violet[500],
    inchworm: primitiveColors.inchworm[500],
    aqua: primitiveColors.aqua[500]
  }
} as const;

export type Colors = typeof colors;
